import React, { useEffect, useRef, useState } from "react";
import { cx } from "./cx";

// DropdownMenu — click trigger to toggle a menu. Closes on click-outside,
// Escape, or item select.
// trigger: node. items: [{ label, onClick, icon?, danger?, disabled? }]. align? "left"|"right".
//
// REFINEMENT NOTES:
// - Item hover is navy-tinted (hover:bg-primary/5 + text-primary) to match Table
//   rows; danger items keep the red 50-tint.
// - Leading icons are muted (text-body/60) so labels lead.
// - focus-visible keyboard highlight on items.
// - disabled items are muted and non-interactive (e.g. a busy driver in the
//   dispatch assign menu) — they stay visible so the reason is readable.
export function DropdownMenu({ trigger, items = [], align = "right" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="relative inline-block" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="inline-flex items-center rounded-btn focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
      >
        {trigger}
      </button>
      {open && (
        <div
          role="menu"
          className={cx(
            "absolute z-30 mt-1.5 min-w-[11rem] rounded-card border border-border bg-white shadow-pop p-1 animate-fade-in",
            align === "left" ? "left-0" : "right-0"
          )}
        >
          {items.map((item, i) => (
            <button
              key={i}
              role="menuitem"
              disabled={item.disabled}
              onClick={() => {
                if (item.disabled) return;
                setOpen(false);
                item.onClick?.();
              }}
              className={cx(
                "w-full flex items-center gap-2.5 px-3 py-2 text-sm rounded-btn text-left transition-colors",
                "focus-visible:outline-none focus-visible:bg-surface",
                item.disabled
                  ? "text-body/35 cursor-not-allowed"
                  : item.danger
                  ? "text-danger hover:bg-danger-50"
                  : "text-body hover:bg-primary/5 hover:text-primary"
              )}
            >
              {item.icon && (
                <span className="shrink-0 inline-flex text-body/60">{item.icon}</span>
              )}
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
